import React from 'react';
import { Input, Modal, Button, notification } from 'antd';
import {
  PlusOutlined,
  FolderOpenOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { IDataArea, IData } from 'store/ducks/DataEntry/ICTServices/types';
import { EditableColumnType } from 'components/CustomTable/CustomTable';
import CustomSelect from 'components/CustomSelect';
import DynamicField from 'components/DynamicCalendarField/DynamicField';
import * as Styled from '../Tables/DataEntry/DataEntry.styled';
import { deleteRecord } from 'utils/hooks/deleteRecord';

type GetColumnsProps = {
  data: IDataArea[];
  countries: { label: string; value: string }[];
  sensitivenessOfData: { label: string; value: string }[];
  editedData: Record<string, any>;
  isTooltipVisible: Record<string, boolean>;
  handleEditChange: (
    e: React.ChangeEvent<HTMLInputElement>,
    fieldName: string,
    rowKey: string | number,
  ) => void;
  handleOpenTooltip: (field: string) => void;
  handleCloseTooltip: (field: string) => void;
  setData: (data: IDataArea[]) => void;
  fetchData: () => void;
  addNewData: (key: number) => void;
  t: (key: string) => string;
};

export const getEditableColumns = ({
  data,
  countries,
  sensitivenessOfData,
  editedData,
  isTooltipVisible,
  handleEditChange,
  handleOpenTooltip,
  handleCloseTooltip,
  setData,
  fetchData,
  addNewData,
  t,
}: GetColumnsProps): EditableColumnType<IDataArea>[] => [
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p> {t('DATA.ICT_SYSTEM')} </p>
        </div>
      </Styled.TitleComponent>
    ),
    dataIndex: 'ict_system',
    editable: true,
    className: 'highlighted-column',
    render: (_: any, record: IDataArea) => {
      const fieldName = `${record.ict_system}`;
      const rowKey = record.key;
      return (
        <Input
          value={
            editedData.hasOwnProperty(fieldName)
              ? editedData[fieldName]
              : record.ict_system
          }
          onChange={(e) => handleEditChange(e, fieldName, rowKey)}
        />
      );
    },
  },
  {
    title: t('DATA.DATA'),
    dataIndex: 'data',
    key: 'data',
    render: (_, record: IDataArea) => (
      <div>
        {record.data.map((itemData: IData, index: number) => {
          const {
            data_id,
            data_description,
            data_location_of_data_at_rest,
            data_location_of_data_management,
            data_start_date,
            data_end_date,
            sensitivenessOfDataStoredbyICTTPSP_name,
            site,
          } = itemData;

          const rowKey = record.key;

          const getFieldName = (suffix: string) =>
            `${data_id}_newItem_${suffix}`;

          const idFieldName = getFieldName('data_id');
          const descriptionFieldName = getFieldName('data_description');
          const restLocationFieldName = getFieldName(
            'data_location_of_data_at_rest',
          );
          const managementLocationFieldName = getFieldName(
            'data_location_of_data_management',
          );
          const startDateFieldName = getFieldName('data_start_date');
          const endDateFieldName = getFieldName('data_end_date');
          const sensitivenessName = getFieldName(
            'sensitivenessOfDataStoredbyICTTPSP_name',
          );
          const sensitivenessId = getFieldName(
            'sensitivenessOfDataStoredbyICTTPSP_id',
          );
          const siteFieldName = getFieldName('site');

          const handleRemoveData = (dataKey: number, dataID: number) => {
            Modal.confirm({
              title: t('DATA.SURE'),
              content: t('DATA.ACTION_CANNOT_BE_UNDONE'),
              okText: t('GENERAL.YES'),
              cancelText: t('GENERAL.NO'),
              onOk: async () => {
                try {
                  deleteRecord({
                    id: dataID,
                    tableName: 'data',
                    onSuccess: async () => {
                      const updatedDatas = data.map((d) =>
                        d.key === dataKey
                          ? {
                              ...d,
                              data: d.data.filter((d) => d.data_id !== dataID),
                            }
                          : d,
                      );
                      setData(updatedDatas);
                      fetchData();
                    },
                  });
                } catch (error) {
                  console.log('Failed to remove Data:', error);
                }
              },
              onCancel: () => {
                notification.info({
                  message: t('GENERAL.INFO'),
                  description: t('DATA.REMOVAL_CANCELLED'),
                  duration: 10,
                });
              },
            });
          };

          return (
            <Styled.TooltipContainer
              key={data_id || index}
              visible={isTooltipVisible[idFieldName]}
              title={
                <div>
                  <Styled.TooltipButtonContainer>
                    <Button
                      size="small"
                      type="text"
                      danger
                      onClick={() => handleRemoveData(rowKey, data_id)}
                    >
                      <DeleteOutlined />
                    </Button>
                    <Button
                      size="small"
                      type="text"
                      onClick={() => handleCloseTooltip(idFieldName)}
                    >
                      {t('GENERAL.ADD_AMEND')}
                    </Button>
                  </Styled.TooltipButtonContainer>

                  <Styled.InputField>
                    <strong>
                      <Styled.TitleComponent>
                        {t('DATA.LOCATION_OF_DATA_AT_REST')}
                        <Styled.Asterisx>*</Styled.Asterisx>
                        {/* <AdditionalInformation description="..." /> */}
                      </Styled.TitleComponent>
                    </strong>
                    <CustomSelect
                      options={countries}
                      value={
                        countries.find(
                          (c) =>
                            c.value ===
                            (editedData[restLocationFieldName] ??
                              data_location_of_data_at_rest),
                        )?.label
                      }
                      onChange={(_: any, option: { value: any }) =>
                        handleEditChange(
                          {
                            target: { value: option.value },
                          } as React.ChangeEvent<HTMLInputElement>,
                          restLocationFieldName,
                          rowKey,
                        )
                      }
                      filterOption={(
                        input: string,
                        option: { label: string },
                      ) =>
                        option?.label
                          ?.toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    />
                  </Styled.InputField>

                  <Styled.InputField>
                    <strong>
                      <Styled.TitleComponent>
                        {t('DATA.LOCATION_OF_DATA_MANAGEMENT')}
                        <Styled.Asterisx>*</Styled.Asterisx>
                        {/* <AdditionalInformation description="..." /> */}
                      </Styled.TitleComponent>
                    </strong>
                    <CustomSelect
                      options={countries}
                      value={
                        countries.find(
                          (c) =>
                            c.value ===
                            (editedData[managementLocationFieldName] ??
                              data_location_of_data_management),
                        )?.label
                      }
                      onChange={(_: any, option: { value: any }) =>
                        handleEditChange(
                          {
                            target: { value: option.value },
                          } as React.ChangeEvent<HTMLInputElement>,
                          managementLocationFieldName,
                          rowKey,
                        )
                      }
                      filterOption={(
                        input: string,
                        option: { label: string },
                      ) =>
                        option?.label
                          ?.toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    />
                  </Styled.InputField>

                  <Styled.InputField>
                    <strong>
                      <Styled.TitleComponent>
                        {t('DATA.SENSITIVENESS_DATA_STORED')}
                        <Styled.Asterisx>*</Styled.Asterisx>
                        {/* <AdditionalInformation description="..." /> */}
                      </Styled.TitleComponent>
                    </strong>
                    <CustomSelect
                      options={sensitivenessOfData}
                      value={
                        editedData[sensitivenessName] ??
                        sensitivenessOfDataStoredbyICTTPSP_name
                      }
                      onChange={(
                        _: any,
                        option: { value: any; label: any },
                      ) => {
                        handleEditChange(
                          {
                            target: { value: option.value },
                          } as React.ChangeEvent<HTMLInputElement>,
                          sensitivenessId,
                          rowKey,
                        );
                        handleEditChange(
                          {
                            target: { value: option.label },
                          } as React.ChangeEvent<HTMLInputElement>,
                          sensitivenessName,
                          rowKey,
                        );
                      }}
                      filterOption={(
                        input: string,
                        option: { label: string },
                      ) =>
                        option?.label
                          ?.toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    />
                  </Styled.InputField>
                  <Styled.InputField>
                    <strong>
                      {t('DATA.DATA_VALID_FROM')}{' '}
                      <Styled.Asterisx>*</Styled.Asterisx>
                    </strong>
                    <DynamicField
                      value={editedData[startDateFieldName] ?? data_start_date}
                      fieldName={startDateFieldName}
                      rowKey={rowKey}
                      onChange={(e) =>
                        handleEditChange(e, startDateFieldName, rowKey)
                      }
                      type="date"
                    />
                  </Styled.InputField>
                  <Styled.InputField>
                    <strong>
                      {t('DATA.DATA_VALID_TO')}{' '}
                      <Styled.Asterisx>*</Styled.Asterisx>
                    </strong>
                    <DynamicField
                      value={editedData[endDateFieldName] ?? data_end_date}
                      fieldName={endDateFieldName}
                      rowKey={rowKey}
                      onChange={(e) =>
                        handleEditChange(e, endDateFieldName, rowKey)
                      }
                      type="date"
                      useDefaultDate
                    />
                  </Styled.InputField>
                  <Styled.InputField>
                    <strong> {t('DATA.SITE')}</strong>
                    <Input
                      value={editedData[siteFieldName] ?? site}
                      onChange={(e) =>
                        handleEditChange(e, siteFieldName, rowKey)
                      }
                    />
                  </Styled.InputField>
                  <Styled.InputField>
                    <strong>{t('DATA.DESCRIPTION')}</strong>
                    <Input
                      value={
                        editedData[descriptionFieldName] ?? data_description
                      }
                      onChange={(e) =>
                        handleEditChange(e, descriptionFieldName, rowKey)
                      }
                    />
                  </Styled.InputField>
                </div>
              }
              open={isTooltipVisible[idFieldName]}
              trigger={[]}
            >
              <Styled.TagComponent
                color="blue"
                onClick={() => handleOpenTooltip(idFieldName)}
              >
                <Input
                  value={editedData[descriptionFieldName] ?? data_description}
                  readOnly
                  addonAfter={<FolderOpenOutlined />}
                />
              </Styled.TagComponent>
              <Styled.GlobalStyle />
            </Styled.TooltipContainer>
          );
        })}
        <Styled.AddButton
          type="text"
          icon={<PlusOutlined />}
          onClick={() => addNewData(record.key)}
        />
      </div>
    ),
  },
];
